import { useState } from "react"

import { graphql, useStaticQuery } from "gatsby"
import { GatsbyImage, IGatsbyImageData } from "gatsby-plugin-image"
import { Trans } from "gatsby-plugin-react-i18next"
import gsap from "gsap"
import { ScrollTrigger } from "gsap/all"
import styled, { css } from "styled-components"

import { ReactComponent as CloseModal } from "images/homepage/Reinventing/Close_Modal.svg"
import { ReactComponent as Success } from "images/homepage/Reinventing/Success.svg"
import colors from "styles/colors"
import media from "styles/media"
import text from "styles/text"
import { fmobile, fresponsive, ftablet } from "utils/fullyResponsive"
import useAnimation from "utils/useAnimation"
import useMedia from "utils/useMedia"

const HEIGHT_MULTIPLIER = 0.86

interface Props {
  allAnimationImages: Record<string, IGatsbyImageData | undefined>
}

export default function Reinventing({ allAnimationImages }: Props) {
  const [wrapper, setWrapper] = useState<HTMLDivElement | null>(null)
  const [content, setContent] = useState<HTMLDivElement | null>(null)
  const [fakeModal, setFakeModal] = useState<HTMLDivElement | null>(null)
  const [mainCardEl, setMainCardEl] = useState<HTMLDivElement | null>(null)
  const [modalText, setModalText] = useState<HTMLElement | null>(null)
  const [fakeButton, setFakeButton] = useState<HTMLElement | null>(null)
  const [iPhoneWrapper, setIPhoneWrapper] = useState<HTMLDivElement | null>(
    null
  )
  const [cardReaderDiv, setCardReaderDiv] = useState<HTMLDivElement | null>(
    null
  )
  const [successIconEl, setSuccessIconEl] = useState<SVGElement | null>(null)
  const [cover, setCover] = useState<HTMLDivElement | null>(null)

  const { iPhone, goodGrocer, mainCard } = allAnimationImages

  const firstStagger = useMedia("=50px", "=3.012vw", "=4.883vw", "=6.667vw")
  const readerValue = useMedia("282px", "16.988vw", "27.539vw", "37.6vw")
  const cardYValue = useMedia("205px", "12.349vw", "20.02vw", "27.467vw")
  const phoneYValue = useMedia("+=128px", "+=7.711vw", "+=12.5vw", "+=17.067vw")
  const cardYValue2 = useMedia("+=155px", "+=9.337vw", "+=15.137vw", "+=20.8vw")
  const phoneValue2 = useMedia(
    "-=190px",
    "-=11.446vw",
    "-=18.555vw",
    "-=25.333vw"
  )
  const goodValue = useMedia("-=65px", "-=3.916vw", "-=6.348vw", "-=8.8vw")
  const successValue = useMedia(
    "-=200px",
    "-=12.048vw",
    "-=19.531vw",
    "-=26.667vw"
  )

  useAnimation(() => {
    if (
      content &&
      wrapper &&
      fakeModal &&
      modalText &&
      fakeButton &&
      mainCardEl &&
      iPhoneWrapper
    ) {
      const payPass =
        ".reinvent-path__1, .reinvent-path__2, .reinvent-path__3, .reinvent-path__4, .reinvent-path__5"
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: wrapper,
          start: "top top",
          end: "bottom bottom",
          scrub: 1,
          pin: content,
          pinSpacing: true,
        },
      })
      ScrollTrigger.refresh()
      gsap.set(successIconEl, { rotate: -180 })
      tl.to(fakeModal, { y: "-=50", opacity: 1, duration: 1 }, 0)
        .to(
          [mainCardEl, modalText, fakeButton],
          { y: `-${firstStagger}`, opacity: 1, stagger: 0.3, duration: 1 },
          1
        )
        .to(fakeModal, { y: `+${firstStagger}`, opacity: 0, duration: 1 }, 4)
        .to(mainCardEl, { scale: 1.7, y: cardYValue, duration: 3 }, 4)
        .to(iPhoneWrapper, { y: 0, opacity: 1, duration: 3 }, 4)
        .to(cardReaderDiv, { y: readerValue, opacity: 1, duration: 1 }, 9)
        .to(iPhoneWrapper, { y: phoneYValue, scale: 0.8, duration: 1 }, 9)
        .to(mainCardEl, { y: cardYValue2, scale: 1.4, duration: 1 }, 9)
        .to(payPass, { opacity: 1, stagger: 0.4, duration: 2 }, 10)
        .to(successIconEl, { scale: 1, rotate: 0, duration: 2 }, 12)
        .to(cover, { opacity: 0.5, duration: 1 }, 12)
        .to(cardReaderDiv, { y: 0, duration: 1 }, 13)
        .to([iPhoneWrapper, mainCardEl], { y: phoneValue2, duration: 1 }, 13)
        .to(successIconEl, { y: successValue, duration: 1 }, 13)
        .to(successIconEl, { scale: 0, opacity: 0, duration: 1 }, 15)
        .to(
          ".good-grocer",
          { scale: 1.2, y: goodValue, opacity: 1, duration: 1 },
          15
        )
        .to(".good-grocer", { opacity: 0, duration: 0.5 }, 18)
        .to(
          ".good-grocer",
          { scale: 0, y: goodValue, opacity: 0, duration: 1 },
          18
        )
        .to(cover, { opacity: 0, duration: 1 }, 18)
        .to(".reinventing-balance", { y: "-100%", duration: 1 }, 18)
        .to(mainCardEl, { opacity: 1, duration: 2 }, 19)
    }
  }, [
    content,
    wrapper,
    fakeModal,
    modalText,
    fakeButton,
    mainCardEl,
    firstStagger,
    iPhoneWrapper,
    cardYValue,
    phoneYValue,
    cardReaderDiv,
    successIconEl,
    cover,
    readerValue,
    phoneValue2,
    cardYValue2,
    goodValue,
    successValue,
  ])

  const animationImages: Queries.AnimationImagesQuery = useStaticQuery(graphql`
    query AnimationImages {
      cardReader: file(
        relativePath: { eq: "homepage/Reinventing/Card_Reader.webp" }
      ) {
        ...localImageNoBlur
      }
    }
  `)

  const cardReader =
    animationImages.cardReader?.childImageSharp?.gatsbyImageData

  return (
    <Wrapper ref={ref => setWrapper(ref)} heightMultiplier={HEIGHT_MULTIPLIER}>
      <Content ref={ref => setContent(ref)}>
        <Title>
          <Trans>Reinventing how people connect with their money.</Trans>
        </Title>
        <Text>
          <Trans>
            Galileo is an open and connected financial technology platform
            operating on a large scale throughout North and South America and
            supporting transactions globally.
          </Trans>
        </Text>

        <ImageContainer>
          {mainCard && (
            <CardDiv ref={setMainCardEl}>
              <MainCard
                className="reinventing-main-card"
                image={mainCard}
                alt="a fake grocery company notification"
              />
              <CardDetails>
                <CardKicker>
                  <Trans>balance</Trans>
                </CardKicker>
                <CardBalance>
                  <span className="reinventing-balance">
                    <Trans>$100.00</Trans>
                  </span>
                  <span className="reinventing-balance">
                    <Trans>$66.25</Trans>
                  </span>
                </CardBalance>
              </CardDetails>
            </CardDiv>
          )}
          <FakeModal ref={setFakeModal}>
            <StyledCloseModal />
            <ModalContent>
              <ModalText ref={setModalText}>
                <Trans>
                  You are authorized to spend up to $100 at Good Grocers.
                </Trans>
              </ModalText>
              <FakeButton ref={setFakeButton}>
                <Trans>Get Started</Trans>
              </FakeButton>
            </ModalContent>
          </FakeModal>
          <CardReaderDiv ref={setCardReaderDiv}>
            <svg
              width="90"
              height="51"
              viewBox="0 0 90 51"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="reinvent-svg"
            >
              <path
                className="reinvent-path__1"
                d="M49.0469 12.7051C48.5 13.1426 47.8437 13.4707 47.1875 13.6895C46.5312 13.9082 45.7656 14.0176 45 14.0176C44.2344 14.0176 43.4688 13.9082 42.8125 13.6895C42.1563 13.4707 41.5 13.1426 40.9531 12.7051C40.1875 12.1582 39.0938 12.2676 38.5469 13.0332C38 13.7988 38.1094 14.8926 38.875 15.4395C39.75 16.0957 40.625 16.5332 41.7188 16.9707C42.8125 17.4082 43.9062 17.5176 45 17.5176C46.0937 17.5176 47.1875 17.2988 48.2813 16.9707C49.2656 16.6426 50.25 16.0957 51.125 15.4395C51.8906 14.8926 52 13.7988 51.4531 13.0332C50.9062 12.2676 49.8125 12.1582 49.0469 12.7051Z"
                fill="white"
              />
              <path
                className="reinvent-path__2"
                d="M52.2187 18.7208C50.1405 20.0333 47.6249 20.7989 44.9999 20.7989C42.3749 20.7989 39.8593 20.0333 37.7812 18.7208C37.0155 18.1739 35.9218 18.502 35.3749 19.2677C34.828 20.1427 35.1562 21.127 35.9218 21.6739C38.5468 23.3145 41.6093 24.2989 44.9999 24.2989C48.2812 24.2989 51.453 23.3145 54.078 21.6739C54.8437 21.127 55.1718 20.0333 54.6249 19.2677C54.078 18.502 53.0937 18.1739 52.2187 18.7208Z"
                fill="white"
              />
              <path
                className="reinvent-path__3"
                d="M55.5 25.0625C52.4375 26.9219 48.8281 27.9062 45 27.9062C41.1719 27.9062 37.5625 26.8125 34.5 25.0625C33.625 24.625 32.6406 24.8437 32.0938 25.7187C31.6563 26.5937 31.875 27.5781 32.75 28.125C36.3594 30.3125 40.5156 31.5156 45 31.5156C49.4844 31.5156 53.6406 30.3125 57.25 28.125C58.125 27.6875 58.3437 26.5937 57.9063 25.7187C57.3594 24.8437 56.375 24.625 55.5 25.0625Z"
                fill="white"
              />

              <path
                className="reinvent-path__4"
                d="M58.7813 31.4062C54.7344 33.7031 50.0313 35.125 45 35.125C39.9688 35.125 35.2656 33.8125 31.2188 31.4062C30.3438 30.9687 29.3594 31.1875 28.8125 32.0625C28.375 32.9375 28.5938 33.9219 29.4688 34.4687C34.0625 37.0937 39.3125 38.625 45 38.625C50.6875 38.625 55.9375 37.0938 60.5313 34.4688C61.4062 34.0313 61.625 32.9375 61.1875 32.0625C60.75 31.1875 59.6563 30.9687 58.7813 31.4062Z"
                fill="white"
              />

              <path
                className="reinvent-path__5"
                d="M0.50001 25.5C0.50001 18.8605 5.35007 12.7444 13.423 8.2594C21.479 3.78383 32.6422 0.999995 45 0.999996C57.3578 0.999997 68.521 3.78384 76.577 8.25941C84.6499 12.7444 89.5 18.8605 89.5 25.5C89.5 32.1395 84.6499 38.2556 76.577 42.7406C68.521 47.2162 57.3578 50 45 50C32.6422 50 21.479 47.2162 13.423 42.7406C5.35007 38.2556 0.500009 32.1395 0.50001 25.5Z"
                stroke="white"
              />
            </svg>

            {cardReader && (
              <CardReaderImage image={cardReader} alt="card reader" />
            )}
          </CardReaderDiv>
          {iPhone && (
            <IPhoneWrapper ref={setIPhoneWrapper}>
              {goodGrocer && (
                <GoodGrocer
                  image={goodGrocer}
                  className="good-grocer"
                  alt="example grocery spending notification"
                />
              )}

              <Cover ref={setCover} />
              <StyledIphone
                image={iPhone}
                alt="iphone with Galileo Wallet app"
              />
            </IPhoneWrapper>
          )}
          <SuccessIcon ref={setSuccessIconEl} />
        </ImageContainer>
      </Content>
    </Wrapper>
  )
}

const Wrapper = styled.div<{ heightMultiplier: number }>`
  width: 100%;
  position: relative;
  height: calc(400vh * ${({ heightMultiplier }) => heightMultiplier});

  ${media.fullWidth} {
    padding: 0 calc((100vw - 900px) / 2);
  }
  ${media.desktop} {
    padding: 0 22.892vw;
  }
  ${media.tablet} {
    padding: 0 11.328vw;
  }
  ${media.mobile} {
    padding: 0;
  }
`

const Content = styled.div`
  position: relative;
  height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  ${fresponsive(css`
    width: 900px;
  `)}

  ${ftablet(css`
    width: 792px;
  `)}

  ${fmobile(css`
    position: absolute;
    width: 375px;
    max-width: 375px;
  `)}
`

const Title = styled.h4`
  ${text.h4};
  color: ${colors.white};
  text-align: center;

  ${fresponsive(css`
    width: 755px;
    margin-bottom: 40px;
  `)}

  ${ftablet(css`
    width: 600px;
  `)}

  ${fmobile(css`
    ${text.h1Mobile};
    width: 335px;
    text-align: left;
    margin-left: 0;
  `)}
`

const Text = styled.p`
  ${text.bodyR};
  color: ${colors.blue50};
  text-align: center;
  width: 37.771vw;

  ${media.fullWidth} {
    width: 627px;
  }
  ${media.tablet} {
    width: 43.945vw;
  }
  ${media.mobile} {
    width: 89.333vw;
    text-align: left;
    margin-left: 0;
  }
`

const ImageContainer = styled.div`
  position: relative;
  background: linear-gradient(
    103.56deg,
    #05a1c9 9.72%,
    #3d50fc 51.57%,
    #d80c9a 90.28%
  );
  overflow: hidden;
  ${fresponsive(css`
    width: 900px;
    height: 600px;
    border-radius: 40px;
    margin-top: 50px;
  `)}

  ${fmobile(css`
    width: 100%;
    height: 337px;
    border-radius: 0;
  `)}
`

const CardDiv = styled.div`
  position: absolute;
  opacity: 0;
  z-index: 5;
  top: 0;
  left: 50%;
  ${fresponsive(css`
    width: 235px;
    height: 149px;
    top: 165px;
    transform: translateX(-50%);
  `)}

  ${fmobile(css`
    width: 118px;
    height: 75px;
    top: 83px;
  `)}
`

const MainCard = styled(GatsbyImage)`
  width: 100%;
  height: 100%;
  z-index: 1;
`

const CardDetails = styled.div`
  position: absolute;
  z-index: 3;
  ${fresponsive(css`
    top: 17px;
    left: 18px;
  `)}
  ${fmobile(css`
    top: 9px;
    left: 9px;
  `)}
`

const CardKicker = styled.p`
  font-family: "Plain";
  font-style: normal;
  font-weight: 500;
  display: flex;
  align-items: center;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: #ffffff50;

  ${fresponsive(css`
    font-size: 5px;
    line-height: 6px;
  `)}
  ${fmobile(css`
    font-size: 3px;
    line-height: 3px;
  `)}
`

const CardBalance = styled.p`
  position: relative;
  font-family: "Plain";
  font-style: normal;
  font-weight: 300;
  line-height: 150%;
  overflow: hidden;
  color: #ffffff;
  display: flex;
  flex-direction: column;

  span {
    position: relative;
    display: block;
    height: 100%;
  }

  ${fresponsive(css`
    font-size: 14px;
    height: 24px;
  `)}

  ${fmobile(css`
    font-size: 7px;
    height: 12px;
  `)}
`

const FakeModal = styled.div`
  position: absolute;
  background: ${colors.white};
  display: flex;
  justify-content: center;
  align-items: center;
  opacity: 0;

  ${fresponsive(css`
    width: 350px;
    height: 400px;
    left: 275px;
    top: 125px;
    border-radius: 20px;
    padding: 60px 0 40px;
  `)}
  ${fmobile(css`
    width: 175px;
    height: 200px;
    left: 50%;
    transform: translateX(-50%);
    top: 75px;
    border-radius: 10px;
    padding: 30px 0 20px;
  `)}
`

const StyledCloseModal = styled(CloseModal)`
  position: absolute;

  ${fresponsive(css`
    right: 20px;
    top: 20px;

    width: 25px;
    height: 25px;
  `)}

  ${fmobile(css`
    right: 10px;
    top: 10px;

    width: 13px;
    height: 13px;
  `)}
`

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  ${fresponsive(css`
    width: 270px;
  `)}
`

const ModalText = styled.div`
  ${text.bodyR};
  color: ${colors.darkMode};
  text-align: center;
  opacity: 0;
  ${fresponsive(css`
    font-size: 18px;
    width: 259px;
    margin: 171px 0 30px;
    transform: translateY(50px);
  `)}
  ${fmobile(css`
    font-size: 9px;
    width: 129px;
    margin: 86px 0 15px;
    transform: translateY(25px);
  `)}
`

const FakeButton = styled.div`
  background: ${colors.blue400};
  ${text.buttonS};
  color: ${colors.white};
  display: flex;
  justify-content: center;
  align-items: center;
  opacity: 0;
  ${fresponsive(css`
    width: 270px;
    height: 48px;
    border-radius: 55px;
    transform: translateY(50px);
  `)}

  ${fmobile(css`
    width: 135px;
    height: 24px;
    border-radius: 28px;
    transform: translateY(25px);
    font-size: 10px;
  `)}
`

const CardReaderDiv = styled.div`
  position: absolute;

  svg {
    position: absolute;
    z-index: 4;

    path {
      opacity: 0.5;
    }

    ${fresponsive(css`
      width: 90px;
      height: 50px;
      bottom: 92px;
      left: 105px;
    `)}

    ${fmobile(css`
      width: 45px;
      height: 25px;
      bottom: 46px;
      left: 53px;
    `)}
  }

  ${fresponsive(css`
    width: 300px;
    height: 518px;
    left: 300px;
    top: -518px;
  `)}

  ${fmobile(css`
    width: 150px;
    height: 259px;
    left: 112px;
    top: -259px;
  `)}
`

const CardReaderImage = styled(GatsbyImage)`
  width: 100%;
  height: 100%;
`

const IPhoneWrapper = styled.div`
  position: absolute;
  display: flex;
  align-self: center;
  z-index: 1;
  ${fresponsive(css`
    width: 450px;
    height: 950px;
    left: 50%;
    top: 90px;
    transform: translate(-50%, 510px);
  `)}

  ${fmobile(css`
    width: 225px;
    height: 475px;
    left: 50%;
    top: 45px;
    transform: translate(-50%, 300px);
  `)}
`

const GoodGrocer = styled(GatsbyImage)`
  position: absolute;
  left: 50%;
  transform: translateX(-50%) scale(0);

  transform-origin: center center;
  z-index: 8;

  ${fresponsive(css`
    width: 326px;
    height: 68px;
    top: 100px;
  `)}

  ${fmobile(css`
    width: 163px;
    height: 34px;
    top: 50px;
  `)}
`

const Cover = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  z-index: 5;
  opacity: 0;
  background: black;
  ${fresponsive(css`
    border-radius: 50px;
  `)}
  ${fmobile(css`
    border-radius: 25px;
  `)}
`

const StyledIphone = styled(GatsbyImage)`
  position: absolute;
  z-index: 1;
  width: 100%;
  height: 100%;
`

const SuccessIcon = styled(Success)`
  position: absolute;
  transform: scale(0);
  transform-origin: center center;
  z-index: 5;

  ${fresponsive(css`
    width: 64px;
    height: 64px;
    top: 341px;
    left: 418px;
  `)}
  ${fmobile(css`
    width: 32px;
    height: 32px;
    top: 171px;
    left: 175px;
  `)}
`
